import React, { useState } from 'react';
import { TicketIcon, CreditCardIcon, FileCheckIcon, UsersIcon, ArrowUpRightIcon, ArrowDownRightIcon, MoreHorizontalIcon, CalendarIcon, MapPinIcon, WalletIcon, ClipboardListIcon, HeadphonesIcon, TrendingUpIcon, PlaneIcon, StarIcon } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { Link } from 'react-router-dom';
export function DashboardPage() {
  const {
    travelRequests,
    confirmedTrips,
    operationsQueue,
    paymentInfo,
    events,
    supportCases
  } = useAppContext();
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 3000);
  };
  const pendingCount = travelRequests.filter(r => r.status === 'pending').length;
  const creditPercentage = Math.round(paymentInfo.usedCredit / paymentInfo.creditLimit * 100);
  const openCases = supportCases.filter(c => c.status === 'open' || c.status === 'in_progress').length;
  const upcomingEvents = events.filter(e => e.featured).slice(0, 2);
  const recentBookings = [...travelRequests.map(req => ({
    id: req.id,
    user: req.requester,
    destination: req.destination,
    date: req.submittedAt,
    amount: `$${req.amount}`,
    status: req.status === 'approved' ? 'Confirmed' : req.status === 'pending' ? 'Pending' : 'Rejected'
  })), {
    id: 'BK-7829',
    user: 'Sarah Connor',
    destination: 'London (LHR)',
    date: 'Oct 24, 2023',
    amount: '$1,240',
    status: 'Confirmed'
  }, {
    id: 'BK-7830',
    user: 'John Smith',
    destination: 'New York (JFK)',
    date: 'Oct 25, 2023',
    amount: '$850',
    status: 'Pending'
  }].slice(0, 5);
  const upcomingTrips = [...confirmedTrips.map(trip => ({
    id: trip.id,
    title: trip.title,
    location: trip.location,
    date: trip.dates,
    travelers: 1
  })), {
    id: 101,
    title: 'Tech Conference',
    location: 'Las Vegas, NV',
    date: 'Dec 02-05',
    travelers: 12
  }].slice(0, 3);
  const departmentBudgets = [{
    name: 'Engineering',
    spent: 120000,
    total: 150000,
    percent: 80,
    color: 'bg-amber-500'
  }, {
    name: 'Sales',
    spent: 184000,
    total: 200000,
    percent: 92,
    color: 'bg-red-500'
  }, {
    name: 'Marketing',
    spent: 45000,
    total: 100000,
    percent: 45,
    color: 'bg-navy-600'
  }, {
    name: 'Operations',
    spent: 61000,
    total: 80000,
    percent: 76,
    color: 'bg-amber-500'
  }, {
    name: 'HR',
    spent: 18000,
    total: 50000,
    percent: 36,
    color: 'bg-navy-600'
  }];
  return <div className="space-y-5 sm:space-y-6" data-id="element-1076">
      {/* Toast */}
      {toastMessage && <div className="fixed top-20 right-4 sm:right-6 z-50 animate-in slide-in-from-top-2" data-id="element-1077">
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-4 shadow-lg flex items-center gap-3" data-id="element-1078">
            <ArrowUpRightIcon className="h-5 w-5 text-emerald-600 flex-shrink-0" data-id="element-1079" />
            <p className="font-medium text-sm" data-id="element-1080">{toastMessage}</p>
          </div>
        </div>}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-1081">
        <div data-id="element-1082">
          <h2 className="text-xl sm:text-2xl font-bold text-navy-900" data-id="element-1083">
            Dashboard Overview
          </h2>
          <p className="text-sm text-slate-500" data-id="element-1084">
            Welcome back, here's what's happening today.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2" data-id="element-1085">
          <button onClick={() => showToast('Report downloaded successfully')} className="w-full sm:w-auto rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-1086">
            Download Report
          </button>
          <Link to="/booking" className="w-full sm:w-auto rounded-lg bg-navy-900 px-4 py-2 text-sm font-medium text-white hover:bg-navy-700 text-center" data-id="element-1087">
            + New Booking
          </Link>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-6 lg:grid-cols-4" data-id="element-1088">
        <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm" data-id="element-1089">
          <div className="flex items-center justify-between" data-id="element-1090">
            <div className="rounded-full p-2 sm:p-3 bg-navy-50 text-navy-700" data-id="element-1091">
              <TicketIcon className="h-5 w-5 sm:h-6 sm:w-6" data-id="element-1092" />
            </div>
            <div className="flex items-center gap-1 text-xs sm:text-sm font-medium text-emerald-600" data-id="element-1093">
              <ArrowUpRightIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" data-id="element-1094" />
              +12%
            </div>
          </div>
          <div className="mt-3 sm:mt-4" data-id="element-1095">
            <h3 className="text-xl sm:text-2xl font-bold text-navy-900" data-id="element-1096">
              1,248
            </h3>
            <p className="text-xs sm:text-sm text-slate-500" data-id="element-1097">Tickets Issued</p>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm" data-id="element-1098">
          <div className="flex items-center justify-between" data-id="element-1099">
            <div className="rounded-full p-2 sm:p-3 bg-emerald-50 text-emerald-600" data-id="element-1100">
              <CreditCardIcon className="h-5 w-5 sm:h-6 sm:w-6" data-id="element-1101" />
            </div>
            <div className="flex items-center gap-1 text-xs sm:text-sm font-medium text-emerald-600" data-id="element-1102">
              <ArrowUpRightIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" data-id="element-1103" />
              +8.2%
            </div>
          </div>
          <div className="mt-3 sm:mt-4" data-id="element-1104">
            <h3 className="text-xl sm:text-2xl font-bold text-navy-900" data-id="element-1105">
              $482.5k
            </h3>
            <p className="text-xs sm:text-sm text-slate-500" data-id="element-1106">Total Spend</p>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm" data-id="element-1107">
          <div className="flex items-center justify-between" data-id="element-1108">
            <div className="rounded-full p-2 sm:p-3 bg-amber-50 text-amber-600" data-id="element-1109">
              <FileCheckIcon className="h-5 w-5 sm:h-6 sm:w-6" data-id="element-1110" />
            </div>
            <div className="flex items-center gap-1 text-xs sm:text-sm font-medium text-emerald-600" data-id="element-1111">
              <ArrowDownRightIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" data-id="element-1112" />
              -5%
            </div>
          </div>
          <div className="mt-3 sm:mt-4" data-id="element-1113">
            <h3 className="text-xl sm:text-2xl font-bold text-navy-900" data-id="element-1114">
              {pendingCount}
            </h3>
            <p className="text-xs sm:text-sm text-slate-500" data-id="element-1115">
              Pending Approvals
            </p>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm" data-id="element-1116">
          <div className="flex items-center justify-between" data-id="element-1117">
            <div className="rounded-full p-2 sm:p-3 bg-purple-50 text-purple-600" data-id="element-1118">
              <UsersIcon className="h-5 w-5 sm:h-6 sm:w-6" data-id="element-1119" />
            </div>
            <div className="flex items-center gap-1 text-xs sm:text-sm font-medium text-emerald-600" data-id="element-1120">
              <ArrowUpRightIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" data-id="element-1121" />
              +18%
            </div>
          </div>
          <div className="mt-3 sm:mt-4" data-id="element-1122">
            <h3 className="text-xl sm:text-2xl font-bold text-navy-900" data-id="element-1123">142</h3>
            <p className="text-xs sm:text-sm text-slate-500" data-id="element-1124">
              Active Travelers
            </p>
          </div>
        </div>
      </div>

      {/* Quick Links Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-id="element-1125">
        <Link to="/payments" className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm hover:shadow-md transition-shadow" data-id="element-1126">
          <div className="flex items-center gap-3 mb-3" data-id="element-1127">
            <div className="h-10 w-10 rounded-full bg-gold-50 flex items-center justify-center" data-id="element-1128">
              <WalletIcon className="h-5 w-5 text-gold-600" data-id="element-1129" />
            </div>
            <div data-id="element-1130">
              <p className="text-xs font-semibold text-slate-500 uppercase" data-id="element-1131">
                Credit Balance
              </p>
              <p className="text-lg font-bold text-navy-900" data-id="element-1132">
                ${(paymentInfo.availableCredit / 1000).toFixed(0)}k{' '}
                <span className="text-xs font-normal text-slate-400" data-id="element-1133">
                  available
                </span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-1134">
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-1135">
              <div className={`h-full rounded-full ${creditPercentage > 80 ? 'bg-red-500' : creditPercentage > 60 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{
              width: `${creditPercentage}%`
            }} data-id="element-1136" />
            </div>
            <span className="text-xs font-bold text-slate-600" data-id="element-1137">
              {creditPercentage}%
            </span>
          </div>
        </Link>

        <Link to="/operations" className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm hover:shadow-md transition-shadow" data-id="element-1138">
          <div className="flex items-center gap-3 mb-3" data-id="element-1139">
            <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center" data-id="element-1140">
              <ClipboardListIcon className="h-5 w-5 text-blue-600" data-id="element-1141" />
            </div>
            <div data-id="element-1142">
              <p className="text-xs font-semibold text-slate-500 uppercase" data-id="element-1143">
                Operations Queue
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs" data-id="element-1144">
            <span className="flex items-center gap-1 text-amber-600 font-semibold" data-id="element-1145">
              <span className="h-2 w-2 rounded-full bg-amber-500" data-id="element-1146" />
              {operationsQueue.pendingApprovals} pending
            </span>
            <span className="flex items-center gap-1 text-blue-600 font-semibold" data-id="element-1147">
              <span className="h-2 w-2 rounded-full bg-blue-500" data-id="element-1148" />
              {operationsQueue.awaitingTicketing} ticketing
            </span>
            <span className="flex items-center gap-1 text-emerald-600 font-semibold" data-id="element-1149">
              <span className="h-2 w-2 rounded-full bg-emerald-500" data-id="element-1150" />
              {operationsQueue.ticketed} done
            </span>
          </div>
        </Link>

        <Link to="/support" className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm hover:shadow-md transition-shadow sm:col-span-2 lg:col-span-1" data-id="element-1151">
          <div className="flex items-center gap-3 mb-3" data-id="element-1152">
            <div className="h-10 w-10 rounded-full bg-purple-50 flex items-center justify-center" data-id="element-1153">
              <HeadphonesIcon className="h-5 w-5 text-purple-600" data-id="element-1154" />
            </div>
            <div data-id="element-1155">
              <p className="text-xs font-semibold text-slate-500 uppercase" data-id="element-1156">
                Support Cases
              </p>
              <p className="text-lg font-bold text-navy-900" data-id="element-1157">
                {openCases}{' '}
                <span className="text-xs font-normal text-slate-400" data-id="element-1158">open</span>
              </p>
            </div>
          </div>
          <p className="text-xs text-slate-500" data-id="element-1159">Avg response time: 1.5h</p>
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:gap-6 lg:grid-cols-3" data-id="element-1160">
        {/* Recent Bookings */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm lg:col-span-2" data-id="element-1161">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 sm:px-6 py-4" data-id="element-1162">
            <h3 className="font-semibold text-navy-900" data-id="element-1163">Recent Bookings</h3>
            <Link to="/trips" className="text-sm font-medium text-navy-700 hover:text-navy-900" data-id="element-1164">
              View All
            </Link>
          </div>
          <div className="overflow-x-auto" data-id="element-1165">
            <table className="w-full text-left text-sm min-w-[560px]" data-id="element-1166">
              <thead className="bg-slate-50 text-slate-500" data-id="element-1167">
                <tr data-id="element-1168">
                  <th className="px-4 sm:px-6 py-3 font-medium" data-id="element-1169">Booking ID</th>
                  <th className="px-4 sm:px-6 py-3 font-medium" data-id="element-1170">Traveler</th>
                  <th className="px-4 sm:px-6 py-3 font-medium hidden sm:table-cell" data-id="element-1171">
                    Destination
                  </th>
                  <th className="px-4 sm:px-6 py-3 font-medium" data-id="element-1172">Amount</th>
                  <th className="px-4 sm:px-6 py-3 font-medium" data-id="element-1173">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200" data-id="element-1174">
                {recentBookings.map(booking => <tr key={booking.id} className="hover:bg-slate-50" data-id="element-1175">
                    <td className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-navy-900 text-xs sm:text-sm" data-id="element-1176">
                      {booking.id}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 text-slate-600 text-xs sm:text-sm" data-id="element-1177">
                      {booking.user}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 text-slate-600 text-xs sm:text-sm hidden sm:table-cell" data-id="element-1178">
                      {booking.destination}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 text-slate-600 text-xs sm:text-sm" data-id="element-1179">
                      {booking.amount}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4" data-id="element-1180">
                      <span className={`inline-flex rounded-full px-2 sm:px-2.5 py-0.5 text-[10px] sm:text-xs font-medium ${booking.status === 'Confirmed' ? 'bg-emerald-100 text-emerald-800' : booking.status === 'Pending' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`} data-id="element-1181">
                        {booking.status}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        {/* Upcoming Trips */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-1182">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 sm:px-6 py-4" data-id="element-1183">
            <h3 className="font-semibold text-navy-900" data-id="element-1184">Upcoming Trips</h3>
            <Link to="/trips" className="text-sm font-medium text-navy-700 hover:text-navy-900" data-id="element-1185">
              Calendar
            </Link>
          </div>
          <div className="p-4 sm:p-6 space-y-5 sm:space-y-6" data-id="element-1186">
            {upcomingTrips.map(trip => <div key={trip.id} className="flex items-start gap-3 sm:gap-4" data-id="element-1187">
                <div className="flex h-10 w-10 sm:h-12 sm:w-12 flex-shrink-0 items-center justify-center rounded-lg bg-navy-50 text-navy-700" data-id="element-1188">
                  <CalendarIcon className="h-5 w-5 sm:h-6 sm:w-6" data-id="element-1189" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-1190">
                  <h4 className="font-medium text-navy-900 text-sm sm:text-base truncate" data-id="element-1191">
                    {trip.title}
                  </h4>
                  <div className="mt-1 flex items-center text-xs sm:text-sm text-slate-500" data-id="element-1192">
                    <MapPinIcon className="mr-1 h-3.5 w-3.5 flex-shrink-0" data-id="element-1193" />
                    <span className="truncate" data-id="element-1194">{trip.location}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs" data-id="element-1195">
                    <span className="font-medium text-slate-600" data-id="element-1196">
                      {trip.date}
                    </span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600" data-id="element-1197">
                      {trip.travelers} Travelers
                    </span>
                  </div>
                </div>
              </div>)}
            <Link to="/trips" className="block w-full rounded-lg border border-slate-200 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-navy-900 text-center" data-id="element-1198">
              View All Upcoming Trips
            </Link>
          </div>
        </div>
      </div>

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-1199">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 sm:px-6 py-4" data-id="element-1200">
            <h3 className="font-semibold text-navy-900" data-id="element-1201">Upcoming Events</h3>
            <Link to="/events" className="text-sm font-medium text-navy-700 hover:text-navy-900" data-id="element-1202">
              View All Events
            </Link>
          </div>
          <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-id="element-1203">
            {upcomingEvents.map(event => <div key={event.id} className="flex gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg bg-slate-50 border border-slate-100" data-id="element-1204">
                <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-lg bg-cover bg-center flex-shrink-0" style={{
            backgroundImage: `url(${event.image})`
          }} data-id="element-1205" />
                <div className="flex-1 min-w-0" data-id="element-1206">
                  <h4 className="font-semibold text-xs sm:text-sm text-navy-900 truncate" data-id="element-1207">
                    {event.title}
                  </h4>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mt-1" data-id="element-1208">
                    <CalendarIcon className="h-3 w-3 flex-shrink-0" data-id="element-1209" />
                    {event.date}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5" data-id="element-1210">
                    <MapPinIcon className="h-3 w-3 flex-shrink-0" data-id="element-1211" />
                    <span className="truncate" data-id="element-1212">{event.location}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-slate-400 mt-1" data-id="element-1213">
                    <UsersIcon className="h-3 w-3" data-id="element-1214" />
                    {event.attendeeCount} interested
                  </div>
                </div>
              </div>)}
          </div>
        </div>}

      {/* Budget Visualization */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm" data-id="element-1215">
        <div className="mb-5 sm:mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-1216">
          <h3 className="font-semibold text-navy-900" data-id="element-1217">
            Budget Utilization by Department
          </h3>
          <select className="w-full sm:w-auto rounded-lg border border-slate-300 bg-slate-50 px-3 py-1.5 text-sm text-slate-700 focus:border-navy-600 focus:outline-none" data-id="element-1218">
            <option data-id="element-1219">This Quarter</option>
            <option data-id="element-1220">Last Quarter</option>
            <option data-id="element-1221">Year to Date</option>
          </select>
        </div>
        <div className="space-y-4 sm:space-y-5" data-id="element-1222">
          {departmentBudgets.map(dept => <div key={dept.name} data-id="element-1223">
              <div className="flex justify-between items-end mb-1" data-id="element-1224">
                <span className="text-xs sm:text-sm font-medium text-slate-700" data-id="element-1225">
                  {dept.name}
                </span>
                <div className="text-right" data-id="element-1226">
                  <span className="text-xs sm:text-sm font-bold text-navy-900" data-id="element-1227">
                    ${(dept.spent / 1000).toFixed(0)}k
                  </span>
                  <span className="text-[10px] sm:text-xs text-slate-500 ml-1" data-id="element-1228">
                    / ${(dept.total / 1000).toFixed(0)}k
                  </span>
                </div>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2 sm:h-2.5" data-id="element-1229">
                <div className={`h-full rounded-full ${dept.color}`} style={{
              width: `${dept.percent}%`
            }} data-id="element-1230" />
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}